import { ReactNode, useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { useStreamContext } from "@/providers/Stream";
import { useThreads } from "@/providers/use-threads";
import { useUser } from "@/providers/use-user";
import { toast } from "sonner";
import {
  getCommonError,
  getUserError,
  UserError,
} from "@/constants/toast-messages";
import { ConversationSelectionModal } from "./ConversationSelectionModal";
import { type ProjectFormData } from "./NewConversationModal";
import { ChatInput } from "@/features/chat/components/ChatInput";
import { useChatInput } from "@/features/chat/hooks/useChatInput";
import { type AddressData } from "@/services/conversation-service";
import type { ContentBlock } from "@/features/chat/types/messages";
import { StorageProvider } from "@/contexts/StorageContext";
import { createClient } from "@/features/chat/lib/client";
import { clearCaptureSession } from "@/features/chat/services/capture-session-service";
import { fetch } from "@/lib/api";

interface ConversationSelectionGateProps {
  children: ReactNode;
}

interface CaptureModeWrapperProps {
  conversationName: string;
  userId: string;
  conversationId: string;
  onComplete: () => void;
  onCancel: () => void;
}

/**
 * Wrapper component that provides capture mode functionality using ChatInput.
 * Uses useChatInput hook with captureMode enabled for auto-save to Firestore.
 * Loads existing content from Firestore on mount.
 */
function CaptureModeWrapper({
  conversationName,
  userId,
  conversationId,
  onComplete,
  onCancel,
}: CaptureModeWrapperProps) {
  const { t } = useTranslation("common");
  const [initialBlocks, setInitialBlocks] = useState<ContentBlock[]>([]);
  const [isLoadingBlocks, setIsLoadingBlocks] = useState(true);

  // Load existing content blocks from Firestore on mount
  useEffect(() => {
    const loadExistingContent = async () => {
      try {
        const client = createClient(userId);
        const messages = await client.getConversationHistory(conversationId);

        // Extract content blocks from human messages (audio, images, documents)
        const blocks: ContentBlock[] = [];
        for (const msg of messages) {
          if (msg.type === "human" && Array.isArray(msg.content)) {
            for (const block of msg.content) {
              // Only include audio, image, document, and file blocks (not text)
              if (
                block &&
                typeof block === "object" &&
                "type" in block
              ) {
                const blockType = (block as { type: string }).type;
                // Skip text blocks, include media blocks
                if (blockType !== "text" && blockType !== "tool_result" && blockType !== "tool_use") {
                  blocks.push(block as ContentBlock);
                }
              }
            }
          }
        }

        console.log("[CaptureMode] Loaded existing blocks:", blocks.length);
        setInitialBlocks(blocks);
      } catch (error) {
        console.error("[CaptureMode] Failed to load existing content:", error);
        // Continue without existing content on error
      } finally {
        setIsLoadingBlocks(false);
      }
    };

    loadExistingContent();
  }, [userId, conversationId]);

  // Use the unified chat input hook with capture mode enabled
  const chatInputProps = useChatInput({
    messages: [], // No messages in capture mode
    isLoading: false,
    artifactContext: {},
    onSubmit: () => {}, // No-op in capture mode
    onResetFirstToken: () => {},
    userId,
    conversationId,
    captureMode: true, // Enable auto-save to Firestore
    initialContentBlocks: initialBlocks,
  });

  // Navigate to chat immediately when user clicks "Rapport opstellen"
  const handleComplete = useCallback(() => {
    console.log("[CaptureMode] User clicked Rapport Opstellen - navigating to chat immediately...");
    onComplete();
  }, [onComplete]);

  // Show loading state while loading existing content
  if (isLoadingBlocks) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <StorageProvider overrideUserId={userId} overrideConversationId={conversationId}>
      <ChatInput
        mode="capture"
        {...chatInputProps}
        isLoading={false}
        onStop={() => {}}
        onCaptureComplete={handleComplete}
        onCaptureCancel={onCancel}
        conversationName={conversationName}
      />
    </StorageProvider>
  );
}

/**
 * Gate that ensures a conversation is selected before accessing the app.
 * - If threadId exists: renders children
 * - If no threadId: shows conversation selection modal
 * - Automatically shows modal after login if no conversation is selected
 */
export function ConversationSelectionGate({ children }: ConversationSelectionGateProps) {
  const { t } = useTranslation("common");
  const { threadId, setThreadId, submit, setPendingSubmit } = useStreamContext();
  const { threads, threadsLoading } = useThreads();
  const { userId, isLoading: userLoading } = useUser();
  const [showModal, setShowModal] = useState(false);
  const [showCaptureMode, setShowCaptureMode] = useState(false);
  const [pendingConversationName, setPendingConversationName] = useState("");
  const [pendingConversationId, setPendingConversationId] = useState("");
  const [_pendingAddress, setPendingAddress] = useState<AddressData | null>(null);
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  // Synchronous ref to prevent duplicate project creation (React state updates are async)
  // This prevents race conditions when sidebar is clicked rapidly
  const creationInProgressRef = useRef(false);
  // Store the specific threadId that should trigger stream after capture mode completion
  // This ensures we only start the stream for the correct thread, not any thread switch
  const [captureCompleteThreadId, setCaptureCompleteThreadId] = useState<string | null>(null);

  // Trigger stream when threadId matches the capture-completed threadId
  useEffect(() => {
    // Only trigger if threadId matches the capture-completed threadId
    // This prevents triggering for unrelated thread switches
    if (captureCompleteThreadId && threadId === captureCompleteThreadId && userId) {
      console.log("[ConversationSelectionGate] ThreadId matches capture-completed thread, loading messages and triggering stream...");
      setCaptureCompleteThreadId(null); // Reset flag

      // Load captured messages from Firestore first, then trigger stream
      const loadAndSubmit = async () => {
        try {
          const client = createClient(userId);
          const messages = await client.getConversationHistory(threadId);

          console.log("[ConversationSelectionGate] Loaded messages from Firestore:", messages.length);

          // Submit without new messages - backend will process existing captured messages
          submit(
            {
              messages: [],
            },
            {
              streamMode: ["values"],
              streamSubgraphs: true,
              streamResumable: true,
              // Optimistically set messages from Firestore
              optimisticValues: (prev: any) => ({
                ...prev,
                messages: messages,
              }),
            }
          );
        } catch (error) {
          console.error("[ConversationSelectionGate] Failed to load messages:", error);
          // Still trigger stream even if loading fails
          submit(
            {
              messages: [],
            },
            {
              streamMode: ["values"],
              streamSubgraphs: true,
              streamResumable: true,
            }
          );
        }
      };

      loadAndSubmit();
    }
  }, [captureCompleteThreadId, threadId, userId, submit]);

  // Helper function to create project via backend
  const createProjectViaBackend = useCallback(async (
    projectName: string,
    projectId: string,
    address: AddressData | null
  ): Promise<boolean> => {
    if (!userId) return false;

    try {
      const response = await fetch("/db/create-project", {
        method: "POST",
        userId,
        body: JSON.stringify({
          user_name: userId,
          project_id: projectId,
          project_name: projectName,
          address: address?.formattedAddress || null,
          address_lat: address?.lat || null,
          address_lng: address?.lng || null,
          address_place_id: address?.placeId || null,
        }),
      });

      if (!response.ok) {
        throw new Error(`Backend error: ${response.status}`);
      }

      // Consume streaming response
      const reader = response.body?.getReader();
      if (reader) {
        while (true) {
          const { done } = await reader.read();
          if (done) break;
        }
      }

      console.log("✅ Project created with welcome message:", projectId);
      return true;
    } catch (error) {
      console.error("❌ Failed to create project via backend:", error);
      toast.error(getCommonError(), {
        description: error instanceof Error ? error.message : "Failed to create project",
      });
      return false;
    }
  }, [userId]);

  // Show modal if user is loaded and no threadId is set
  // Also check for pending project from sidebar (stored in sessionStorage)
  useEffect(() => {
    if (!userLoading && userId && !threadId && !threadsLoading) {
      // Synchronous check to prevent duplicate creation (state updates are async)
      if (creationInProgressRef.current) {
        return;
      }

      // Check if there's a pending project name from sidebar
      const pendingName = sessionStorage.getItem("pendingProjectName");
      if (pendingName) {
        // CRITICAL: Set synchronous lock BEFORE clearing sessionStorage
        // This prevents race conditions from rapid sidebar clicks
        creationInProgressRef.current = true;

        // Clear it immediately so we don't trigger again
        sessionStorage.removeItem("pendingProjectName");

        // Also check for pending address
        const pendingAddressJson = sessionStorage.getItem("pendingProjectAddress");
        sessionStorage.removeItem("pendingProjectAddress");
        let address: AddressData | null = null;
        if (pendingAddressJson) {
          try {
            address = JSON.parse(pendingAddressJson) as AddressData;
          } catch (e) {
            console.warn("Failed to parse pending address:", e);
          }
        }

        // Generate UUID and create project via backend
        const newProjectId = uuidv4();
        console.log("🎬 Found pending project from sidebar:", pendingName, "Generated ID:", newProjectId, "Address:", address?.formattedAddress);

        // Show loading state while creating project
        setIsCreatingProject(true);

        createProjectViaBackend(pendingName, newProjectId, address).then((success) => {
          // Release the lock
          creationInProgressRef.current = false;
          setIsCreatingProject(false);
          if (success) {
            setPendingConversationName(pendingName);
            setPendingConversationId(newProjectId);
            setPendingAddress(address);
            setShowCaptureMode(true);
          } else {
            // On error, show the modal so user can try again
            setShowModal(true);
          }
        });
      } else {
        setShowModal(true);
      }
    }
  }, [userId, threadId, userLoading, threadsLoading, createProjectViaBackend]);

  // If conversation is selected and we're not in capture mode, render the app
  if (threadId && !showCaptureMode) {
    return <>{children}</>;
  }

  // Show loading state while user or threads are loading
  if (userLoading || threadsLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  // Handle conversation creation - create project via backend and show capture mode
  const handleCreateProject = async (data: ProjectFormData) => {
    console.log("📝 handleCreateProject called with:", data);

    if (!userId) {
      toast.error(getCommonError(), {
        description: getUserError(UserError.NOT_FOUND),
      });
      return;
    }

    const newProjectId = uuidv4();
    console.log("📝 Creating project with ID:", newProjectId);

    const success = await createProjectViaBackend(data.zaaknummer, newProjectId, data.address);

    if (success) {
      setPendingConversationName(data.zaaknummer);
      setPendingConversationId(newProjectId);
      setPendingAddress(data.address);
      setShowModal(false);
      setShowCaptureMode(true);
    }
  };

  // Handle capture mode completion - navigate to chat immediately and start stream
  const handleCaptureComplete = async () => {
    console.log("[ConversationSelectionGate] User clicked Rapport Opstellen - setting pending submit and navigating...");

    // Store the threadId we're completing capture for BEFORE clearing pendingConversationId
    const captureThreadId = pendingConversationId;

    // Clear capture session storage - capture is complete, no need to restore
    clearCaptureSession(captureThreadId);

    // Set flag in sessionStorage to show capture completion dialog in ChatConvo
    sessionStorage.setItem('showCaptureCompletionDialog', 'true');

    // CRITICAL: Set isSubmitting=true BEFORE navigation
    // This ensures ChatConvo renders with isSubmitting=true on first render
    // which allows the loader to appear immediately
    setPendingSubmit(true);

    // Set threadId to navigate to the chat view
    setThreadId(captureThreadId);

    // Store the specific threadId that should trigger stream
    // This ensures we only start the stream for this specific capture-completed thread
    setCaptureCompleteThreadId(captureThreadId);

    // Finish capture mode (clears pending state)
    setShowCaptureMode(false);
    setPendingConversationName("");
    setPendingConversationId("");
    setPendingAddress(null);
  };

  // Handle capture mode cancel
  const handleCaptureCancel = () => {
    setShowCaptureMode(false);
    // Only show modal if we don't have an existing thread
    if (!threadId) {
      setShowModal(true);
    }
    setPendingConversationName("");
    setPendingConversationId("");
    setPendingAddress(null);
  };

  // Show capture mode with pre-generated UUID (project created on completion)
  if (showCaptureMode && userId && pendingConversationId) {
    return (
      <CaptureModeWrapper
        conversationName={pendingConversationName}
        userId={userId}
        conversationId={pendingConversationId}
        onComplete={handleCaptureComplete}
        onCancel={handleCaptureCancel}
      />
    );
  }

  // Show loading state while creating project
  if (isCreatingProject) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">{t("project.creating", "Project aanmaken...")}</p>
        </div>
      </div>
    );
  }

  // Show modal for conversation selection
  return (
    <>
      {showModal && (
        <ConversationSelectionModal
          threads={threads}
          onSelect={(conversationId) => {
            setThreadId(conversationId);
            setShowModal(false);
          }}
          onCreateNew={handleCreateProject}
        />
      )}

      {/* Show placeholder while modal is open */}
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="text-center">
          <p className="text-muted-foreground">{t("project.selectToStart")}</p>
        </div>
      </div>
    </>
  );
}
